import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';
import { Alert, Badge, Button, Checkbox, Progress, Skeleton, Switch, TextField } from '../ui';
import SettingsGroup from './SettingsGroup';
import { useAuth } from '../../context/AuthContext';
import {
  budgetSeverity,
  budgetSharePercent,
  normalizeAllowedProviders,
  parseMonthlyBudgetInput,
  providersRemainingUnderEuOnly,
} from './llmPolicyHelpers';

const SEVERITY_TONE = { ok: undefined, warning: 'warning', exceeded: 'danger' };

function currentMonth() {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

async function authJson(url, token, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options.headers },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

/**
 * Tenant "AI policy" panel (spec: admin-ui stage of the SSRF/compliance
 * hardening run). Reads/writes the structured llm_policy endpoints
 * (routes/secrets.js), never the generic PUT/DELETE /tenant/:key ones — those
 * refuse llm_policy with 400 use_policy_endpoint on purpose.
 *
 * Rendered under AdminPage's "tenant-defaults" section, next to
 * `<AccessTokens scope="tenant">` — this panel is where allowUserKeys and the
 * other switches AccessTokens (scope="me") reads are actually set.
 */
export default function LlmPolicySettings() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [status, setStatus] = useState('loading'); // loading | error | unavailable | ready
  const [loadErrorCode, setLoadErrorCode] = useState(null);
  const [providers, setProviders] = useState([]);
  const [providersError, setProvidersError] = useState(false);
  const [usage, setUsage] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState(false);
  const [draft, setDraft] = useState(null);
  const [budgetText, setBudgetText] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState(null); // null | 'saved' | 'error'

  // Each request on its own: only the policy itself decides whether the form
  // can render. A policy that exists but cannot be read (503
  // policy_unavailable — e.g. KEY_ENCRYPTION_KEY changed) gets its own state
  // with a reset, since the server refuses every LLM call until it is replaced.
  const load = useCallback(async () => {
    setStatus('loading');
    setLoadErrorCode(null);
    const [policyRes, providersRes, usageRes] = await Promise.allSettled([
      authJson('/api/secrets/tenant/policy', token),
      authJson('/api/settings/providers', token),
      authJson(`/api/secrets/tenant/usage?month=${currentMonth()}`, token),
    ]);
    if (policyRes.status === 'rejected') {
      const code = policyRes.reason?.body?.error || policyRes.reason?.message || null;
      setLoadErrorCode(code);
      setStatus(code === 'policy_unavailable' ? 'unavailable' : 'error');
      return;
    }
    const { policy } = policyRes.value;
    setDraft(policy);
    setBudgetText(policy.monthlyTokenBudget != null ? String(policy.monthlyTokenBudget) : '');
    setProviders(providersRes.status === 'fulfilled' ? providersRes.value.providers || [] : []);
    setProvidersError(providersRes.status === 'rejected');
    setUsage(usageRes.status === 'fulfilled' ? usageRes.value : null);
    setStatus('ready');
  }, [token]);

  // DELETE works without reading the unreadable row; afterwards LLM_POLICY or
  // the defaults apply until the admin saves a new policy.
  async function resetPolicy() {
    setResetting(true);
    setResetError(false);
    try {
      await authJson('/api/secrets/tenant/policy', token, { method: 'DELETE' });
      await load();
    } catch {
      setResetError(true);
    } finally {
      setResetting(false);
    }
  }

  useEffect(() => { load(); }, [load]);

  const allProviderIds = useMemo(() => providers.map((p) => p.id), [providers]);
  const selectedProviderIds = draft?.allowedProviders ?? allProviderIds;
  const euRemaining = useMemo(() => {
    const scoped = providers.filter((p) => selectedProviderIds.includes(p.id));
    return providersRemainingUnderEuOnly(scoped);
  }, [providers, selectedProviderIds]);

  function patchDraft(patch) {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  function toggleProvider(id) {
    const next = selectedProviderIds.includes(id)
      ? selectedProviderIds.filter((p) => p !== id)
      : [...selectedProviderIds, id];
    patchDraft({ allowedProviders: normalizeAllowedProviders(next, allProviderIds) });
  }

  function onBudgetChange(text) {
    setBudgetText(text);
    const { value, error } = parseMonthlyBudgetInput(text);
    setFieldErrors((prev) => ({ ...prev, monthlyTokenBudget: error }));
    if (!error) patchDraft({ monthlyTokenBudget: value });
  }

  async function save() {
    if (fieldErrors.monthlyTokenBudget) return;
    setSaving(true);
    setSaveState(null);
    setFieldErrors({});
    try {
      const res = await authJson('/api/secrets/tenant/policy', token, {
        method: 'PUT',
        body: JSON.stringify({ policy: draft }),
      });
      setDraft(res.policy);
      setBudgetText(res.policy.monthlyTokenBudget != null ? String(res.policy.monthlyTokenBudget) : '');
      setSaveState('saved');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? null : s)), 2500);
    } catch (err) {
      if (err.body?.error === 'invalid_policy') {
        setFieldErrors({ [err.body.field || '_']: err.body.message });
      }
      setSaveState('error');
    } finally {
      setSaving(false);
    }
  }

  if (status === 'loading') {
    return (
      <SettingsGroup title={t('admin.llmPolicy')} description={t('admin.llmPolicyDesc')}>
        <div role="status" aria-live="polite">
          <span className="etch-visually-hidden">{t('states.loading')}</span>
          <Skeleton shape="text" />
          <Skeleton shape="text" />
          <Skeleton shape="text" />
        </div>
      </SettingsGroup>
    );
  }

  if (status === 'unavailable') {
    return (
      <SettingsGroup title={t('admin.llmPolicy')}>
        <Alert tone="danger" title={t('admin.llmPolicyUnavailableTitle')}>
          {t('admin.llmPolicyUnavailableHint')}
        </Alert>
        {resetError && <Alert tone="danger" title={t('admin.llmPolicyResetError')} />}
        <div className="etch-row">
          <Button onClick={resetPolicy} disabled={resetting}>{t('admin.llmPolicyReset')}</Button>
          <Button variant="secondary" onClick={load} disabled={resetting}>{t('states.retry')}</Button>
        </div>
      </SettingsGroup>
    );
  }

  if (status === 'error') {
    const denied = loadErrorCode === 'no_tenant_bound' || loadErrorCode === 'HTTP 403' || loadErrorCode === 'HTTP 401';
    return (
      <SettingsGroup title={t('admin.llmPolicy')}>
        <Alert tone={denied ? 'warning' : 'danger'} title={t('admin.llmPolicyLoadError')}>
          {loadErrorCode}
        </Alert>
        <Button variant="secondary" onClick={load}>{t('states.retry')}</Button>
      </SettingsGroup>
    );
  }

  const severity = budgetSeverity(usage?.share);
  const usagePercent = budgetSharePercent(usage?.share);

  return (
    <>
      <SettingsGroup
        title={t('admin.llmPolicy')}
        description={t('admin.llmPolicyDesc')}
        actions={
          <Button variant="ghost" size="compact" onClick={load}>
            {t('settings.refresh')}
          </Button>
        }
      >
        {saveState === 'saved' && (
          <Alert tone="success" title={t('admin.llmPolicySaved')} />
        )}
        {saveState === 'error' && (
          <Alert tone="danger" title={t('admin.llmPolicySaveError')}>
            {fieldErrors._ || fieldErrors[Object.keys(fieldErrors)[0]] || null}
          </Alert>
        )}

        <Switch
          label={t('admin.llmPolicyKeyPrecedence')}
          hint={t('admin.llmPolicyKeyPrecedenceHint')}
          error={fieldErrors.keyPrecedence}
          checked={draft.keyPrecedence === 'tenant_first'}
          onChange={(e) => patchDraft({ keyPrecedence: e.target.checked ? 'tenant_first' : 'gateway_first' })}
        />

        <fieldset className="etch-stack" data-gap="2">
          <legend className="etch-field__label">{t('admin.llmPolicyAllowedProviders')}</legend>
          <p className="etch-field__hint">{t('admin.llmPolicyAllowedProvidersHint')}</p>
          {providersError && <Alert tone="warning" title={t('admin.llmPolicyProvidersLoadError')} />}
          {providers.map((provider) => (
            <Checkbox
              key={provider.id}
              label={provider.name}
              checked={selectedProviderIds.includes(provider.id)}
              onChange={() => toggleProvider(provider.id)}
            />
          ))}
          {fieldErrors.allowedProviders && (
            <span className="etch-field__error" role="alert">{t('admin.llmPolicyAllowedProvidersError')}</span>
          )}
        </fieldset>

        <Switch
          label={t('admin.llmPolicyEuOnly')}
          error={fieldErrors.euOnly}
          checked={Boolean(draft.euOnly)}
          onChange={(e) => patchDraft({ euOnly: e.target.checked })}
          hint={
            <>
              {t('admin.llmPolicyEuOnlyHint')}{' '}
              {euRemaining.length > 0
                ? t('admin.llmPolicyEuOnlyRemaining', { list: euRemaining.map((p) => p.name).join(', ') })
                : t('admin.llmPolicyEuOnlyNone')}{' '}
              {t('admin.llmPolicyEuOnlyOpenAiNote')}{' '}
              {t('admin.llmPolicyEuOnlyOllamaNote')}{' '}
              {t('admin.llmPolicyEuOnlyPersonaNote')}
            </>
          }
        />

        <Switch
          label={t('admin.llmPolicyAllowUserKeys')}
          hint={t('admin.llmPolicyAllowUserKeysHint')}
          error={fieldErrors.allowUserKeys}
          checked={Boolean(draft.allowUserKeys)}
          onChange={(e) => patchDraft({ allowUserKeys: e.target.checked })}
        />

        <Switch
          label={t('admin.llmPolicyPiiRedaction')}
          hint={t('admin.llmPolicyPiiRedactionHint')}
          error={fieldErrors.piiRedaction}
          checked={draft.piiRedaction === 'placeholder'}
          onChange={(e) => patchDraft({ piiRedaction: e.target.checked ? 'placeholder' : 'off' })}
        />

        <TextField
          label={t('admin.llmPolicyBudget')}
          hint={!fieldErrors.monthlyTokenBudget ? t('admin.llmPolicyBudgetHint') : undefined}
          error={fieldErrors.monthlyTokenBudget ? t(fieldErrors.monthlyTokenBudget) : undefined}
          inputMode="numeric"
          value={budgetText}
          onChange={(e) => onBudgetChange(e.target.value)}
        />

        <Button onClick={save} disabled={saving || Boolean(fieldErrors.monthlyTokenBudget)}>
          <Save size={14} aria-hidden="true" />
          <span>{saving ? t('states.retrying') : t('admin.llmPolicySave')}</span>
        </Button>
      </SettingsGroup>

      <SettingsGroup title={t('admin.llmPolicyUsageTitle')} description={t('admin.llmPolicyUsageDesc')}>
        {!usage ? (
          <Alert tone="warning" title={t('admin.llmPolicyUsageLoadError')} />
        ) : usage.budget == null ? (
          <p className="etch-meta">
            {t('admin.llmPolicyUsageNoBudget', { used: usage.totalTokens.toLocaleString() })}
          </p>
        ) : (
          <>
            <Progress
              value={usagePercent ?? 0}
              max={100}
              label={t('admin.llmPolicyUsageTotal', {
                used: usage.totalTokens.toLocaleString(),
                budget: usage.budget.toLocaleString(),
              })}
            />
            {severity === 'exceeded' && (
              <Alert tone="danger" title={t('states.quotaExceeded')}>
                {t('states.quotaExceededHint')}
              </Alert>
            )}
          </>
        )}

        {usage && (
          <h3 className="etch-settings-group__title" style={{ fontSize: 'var(--text-sm)' }}>
            {t('admin.llmPolicyUsageBreakdownTitle')}
          </h3>
        )}
        {!usage ? null : usage.breakdown.length === 0 ? (
          <p className="etch-meta">{t('admin.llmPolicyUsageEmpty')}</p>
        ) : (
          <ul className="etch-stack" data-gap="2" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {usage.breakdown.map((row) => (
              <li key={`${row.feature}-${row.provider}`} className="etch-row">
                <span>
                  <Badge tone={SEVERITY_TONE[severity]}>{row.feature}</Badge> {row.provider}
                </span>
                <span className="etch-meta">{row.totalTokens.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </SettingsGroup>
    </>
  );
}
